import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  DnsInventoryDto,
  DnsPublicationDto,
  DnsWorkspaceDomainDto,
  DnsWorkspaceRecordDto,
  DnsWorkspaceZoneDto,
  SubscriptionDto,
  PortalSubscriptionDto,
} from '@cpm/shared';
import { api } from '../api/client.js';
import { RecordForm, DnsDomainSettings, ZoneForm } from './dns-panels.js';
import { Modal } from './Modal.js';
import { timeAgo } from './charts.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  Select,
  Spinner,
  Tabs,
} from './ui.js';

function Publication({ value, label }: { value: DnsPublicationDto; label: string }) {
  const text =
    value.state === 'unconfigured'
      ? 'Awaiting provider setup'
      : value.state === 'synchronized'
        ? 'Synchronized'
        : value.state[0]!.toUpperCase() + value.state.slice(1);
  return (
    <div className="space-y-1">
      <p>
        {label}:{' '}
        <Badge
          tone={
            value.state === 'synchronized' ? 'green' : value.state === 'error' ? 'red' : 'yellow'
          }
        >
          {text}
        </Badge>
      </p>
      {value.reason && <p className="text-sm text-slate-500">{value.reason}</p>}
      <p className="text-xs text-slate-500">
        {value.checkedAt ? `Checked ${timeAgo(value.checkedAt)}` : 'Not verified yet'}
        {value.publishedAt ? ` · Last published ${timeAgo(value.publishedAt)}` : ''}
      </p>
    </div>
  );
}
function RecordManager({
  record,
  zone,
  onClose,
}: {
  record: DnsWorkspaceRecordDto;
  zone: DnsWorkspaceZoneDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [revision] = useState(zone.revision);
  const remove = useMutation({
    mutationFn: () =>
      api.delete(`/api/dns/records/${record.id}`, {
        expectedRevision: revision,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['dns'] });
      onClose();
    },
  });
  return (
    <Modal title={`${record.fqdn} · ${record.type}`} open onClose={onClose}>
      <div className="space-y-4">
        {record.capability.reason && <p>{record.capability.reason}</p>}
        {record.sourceLink && (
          <Link className="text-blue-600 underline" to={record.sourceLink}>
            Manage {record.sourceLabel}
          </Link>
        )}
        {record.capability.allowed ? (
          <RecordForm
            zoneId={zone.zoneId!}
            zoneName={zone.name}
            expectedRevision={revision}
            existing={record}
            onDone={onClose}
          />
        ) : (
          <pre className="whitespace-pre-wrap break-all text-sm">{record.content}</pre>
        )}
        {record.capability.allowed && (
          <div className="border-t pt-4">
            {confirm ? (
              <>
                <p className="mb-2">
                  Delete this record? Services using it may stop working. Public resolver caches can
                  retain the previous value until its TTL expires.
                </p>
                <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Confirm delete
                </Button>
                <Button variant="ghost" onClick={() => setConfirm(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="danger" onClick={() => setConfirm(true)}>
                Delete record
              </Button>
            )}
          </div>
        )}
        {remove.error && <ErrorNote error={remove.error} />}
      </div>
    </Modal>
  );
}
function Zone({ id, portal }: { id: string; portal: boolean }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [add, setAdd] = useState(false);
  const [manage, setManage] = useState<DnsWorkspaceRecordDto | null>(null);
  const search = params.get('recordSearch') ?? '';
  const type = params.get('recordType') ?? '';
  const source = params.get('recordSource') ?? '';
  function recordFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
  }
  const zone = useQuery({
    queryKey: ['dns', 'workspace-zone', id],
    queryFn: () => api.get<DnsWorkspaceZoneDto>(`/api/dns/workspace/zones/${id}`),
    refetchInterval: 30_000,
  });
  const refresh = useMutation({
    mutationFn: () => api.post(`/api/dns/workspace/zones/${id}/check`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dns'] }),
  });
  const tab = params.get('domainTab') ?? 'records';
  if (zone.isPending) return <Spinner />;
  if (zone.error) return <ErrorNote error={zone.error} />;
  const data = zone.data;
  const records = data.records.filter(
    (row) =>
      (!type || row.type === type) &&
      (!source || row.sourceLabel === source) &&
      `${row.fqdn} ${row.content}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        onClick={() => {
          const next = new URLSearchParams(params);
          next.delete('zoneId');
          next.delete('domainTab');
          setParams(next);
        }}
      >
        ← Domains
      </Button>
      <h2 className="text-xl font-semibold">{data.name}</h2>
      <p className="text-sm text-slate-500">
        {data.subscriptionName}
        {data.customerName ? ` · ${data.customerName}` : ''}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Publication value={data.primary} label="Saved records on primary" />
        <Publication value={data.publication} label="Public publishing" />
      </div>
      <Tabs
        active={tab}
        onChange={(value) => {
          const next = new URLSearchParams(params);
          next.set('domainTab', value);
          setParams(next);
        }}
        tabs={[
          { key: 'records', label: 'Records' },
          { key: 'setup', label: 'Setup & status' },
          ...(data.capabilities.manageSettings.allowed && !portal
            ? [{ key: 'settings', label: 'Domain settings' }]
            : []),
        ]}
      />
      {tab === 'records' && (
        <Card>
          <CardHeader
            title="Records"
            description={`${data.recordCount} records`}
            actions={
              <Button
                disabled={!data.capabilities.manageRecords.allowed}
                title={data.capabilities.manageRecords.reason ?? undefined}
                onClick={() => setAdd(true)}
              >
                Add record
              </Button>
            }
          />
          <div className="space-y-3 px-5 pb-4">
            {data.capabilities.manageRecords.reason && (
              <p className="text-sm text-slate-500">{data.capabilities.manageRecords.reason}</p>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="dns-record-search">Search records</Label>
                <Input
                  id="dns-record-search"
                  value={search}
                  onChange={(e) => recordFilter('recordSearch', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="dns-type-filter">Type</Label>
                <Select
                  id="dns-type-filter"
                  value={type}
                  onChange={(e) => recordFilter('recordType', e.target.value)}
                >
                  <option value="">All types</option>
                  {[...new Set(data.records.map((row) => row.type))].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="dns-source-filter">Source</Label>
                <Select
                  id="dns-source-filter"
                  value={source}
                  onChange={(e) => recordFilter('recordSource', e.target.value)}
                >
                  <option value="">All sources</option>
                  {[...new Set(data.records.map((row) => row.sourceLabel))].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="divide-y">
              {records.map((row) => (
                <div key={row.id} className="grid gap-3 py-4 md:grid-cols-[1fr_2fr_auto]">
                  <div>
                    <p className="break-all font-mono text-sm">{row.fqdn}</p>
                    <p>
                      {row.type} · <Badge tone="gray">{row.sourceLabel}</Badge>
                    </p>
                  </div>
                  <div>
                    <details>
                      <summary className="cursor-pointer break-all text-sm">
                        {row.content.length > 90 ? `${row.content.slice(0, 90)}…` : row.content}
                      </summary>
                      <pre className="mt-2 whitespace-pre-wrap break-all text-xs">
                        {row.content}
                      </pre>
                      <Button
                        variant="ghost"
                        onClick={() => void navigator.clipboard.writeText(row.content)}
                      >
                        Copy value
                      </Button>
                    </details>
                    <p className="mt-1 text-xs text-slate-500">
                      TTL {row.ttl}s
                      {row.effectiveTtl !== row.ttl
                        ? ` · Published TTL ${row.effectiveTtl}s (shared record set)`
                        : ''}
                    </p>
                    {row.capability.reason && (
                      <p className="text-xs text-slate-500">{row.capability.reason}</p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    aria-label={`Manage ${row.fqdn} ${row.type}`}
                    onClick={() => setManage(row)}
                  >
                    Manage
                  </Button>
                </div>
              ))}
            </div>
            {!records.length && <p>No records match this view.</p>}
          </div>
        </Card>
      )}
      {tab === 'setup' && (
        <Card>
          <CardHeader
            title="Setup & status"
            actions={
              <Button
                variant="secondary"
                loading={refresh.isPending}
                onClick={() => refresh.mutate()}
              >
                Refresh status
              </Button>
            }
          />
          <div className="space-y-4 px-5 pb-4">
            <p>
              Your provider configures public DNS publishing. Saving records does not change your
              registrar’s nameserver delegation. Public resolver caches may retain previous values
              until their TTL expires.
            </p>
            <div>
              <h3 className="font-semibold">Public nameservers</h3>
              {data.nameservers.length ? (
                data.nameservers.map((value) => (
                  <p className="font-mono text-sm" key={value}>
                    {value}
                  </p>
                ))
              ) : (
                <p>Awaiting provider setup</p>
              )}
            </div>
            <div>
              <h3 className="font-semibold">DNSSEC</h3>
              <p>
                {data.signingMode
                  ? `Signing: ${data.signingMode === 'panel-signatures' ? 'Primary signatures' : 'Public provider'}`
                  : 'Signing configuration has not been confirmed'}
              </p>
              {data.dsRecords.map((value) => (
                <p className="break-all font-mono text-xs" key={value}>
                  {value}
                </p>
              ))}
              <p className="text-sm text-slate-500">
                Confirm DS and nameserver changes with your provider before changing registrar
                settings.
              </p>
            </div>
            {data.targets.map((value, i) => (
              <Publication key={i} value={value.publication} label={value.label} />
            ))}
            {refresh.error && <ErrorNote error={refresh.error} />}
          </div>
        </Card>
      )}
      {tab === 'settings' && data.capabilities.manageSettings.allowed && !portal && (
        <DnsDomainSettings zoneId={id} />
      )}
      <Modal title={`Add record to ${data.name}`} open={add} onClose={() => setAdd(false)}>
        <RecordForm
          zoneId={id}
          zoneName={data.name}
          expectedRevision={data.revision}
          onDone={() => setAdd(false)}
        />
      </Modal>
      {manage && <RecordManager record={manage} zone={data} onClose={() => setManage(null)} />}
    </div>
  );
}
export function DnsWorkspace({ portal = false }: { portal?: boolean }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [assign, setAssign] = useState(false);
  const [name, setName] = useState('');
  const [assignmentSub, setAssignmentSub] = useState('');
  const [configureZone, setConfigureZone] = useState<DnsWorkspaceDomainDto | null>(null);
  const filter = new URLSearchParams();
  for (const key of ['search', 'page', 'subscriptionId', 'clientId'])
    if (params.get(key)) filter.set(key, params.get(key)!);
  const inventory = useQuery({
    queryKey: ['dns', 'domains', filter.toString()],
    queryFn: () => api.get<DnsInventoryDto>(`/api/dns/domains?${filter}`),
  });
  const subscriptions = useQuery({
    queryKey: ['dns', 'subscription-filters', portal],
    queryFn: () =>
      api.get<Array<SubscriptionDto | PortalSubscriptionDto>>(
        portal ? '/api/portal/subscriptions' : '/api/subscriptions',
      ),
  });
  function change(values: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values))
      value ? next.set(key, value) : next.delete(key);
    setParams(next);
  }
  const creation = useMutation({
    mutationFn: (domain: DnsWorkspaceDomainDto) =>
      api.post<DnsWorkspaceZoneDto>(`/api/dns/domains/${domain.id}/zone`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['dns'] });
      change({ zoneId: data.zoneId });
    },
  });
  const assignment = useMutation({
    mutationFn: () =>
      api.post('/api/dns/domains', {
        name,
        subscriptionId: assignmentSub || params.get('subscriptionId'),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dns'] });
      setAssign(false);
      setName('');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/dns/domains/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dns'] }),
  });
  if (params.get('zoneId'))
    return <Zone key={params.get('zoneId')} id={params.get('zoneId')!} portal={portal} />;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="dns-search">Search domains</Label>
          <Input
            key={params.get('search')}
            id="dns-search"
            defaultValue={params.get('search') ?? ''}
            onBlur={(e) => change({ search: e.target.value, page: null })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') change({ search: e.currentTarget.value, page: null });
            }}
          />
        </div>
        <div>
          <Label htmlFor="dns-subscription-filter">Subscription</Label>
          <Select
            id="dns-subscription-filter"
            value={params.get('subscriptionId') ?? ''}
            onChange={(e) => change({ subscriptionId: e.target.value, page: null })}
          >
            <option value="">All subscriptions</option>
            {subscriptions.data?.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </Select>
        </div>
        {!portal && (
          <div>
            <Label htmlFor="dns-customer-filter">Customer</Label>
            <Select
              id="dns-customer-filter"
              value={params.get('clientId') ?? ''}
              onChange={(e) =>
                change({
                  clientId: e.target.value,
                  subscriptionId: null,
                  page: null,
                })
              }
            >
              <option value="">All customers</option>
              {[
                ...new Map(
                  (subscriptions.data as SubscriptionDto[] | undefined)?.map((sub) => [
                    sub.clientId,
                    sub.clientName,
                  ]),
                ).entries(),
              ].map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      {!portal && <Button onClick={() => setAssign(true)}>Assign domain</Button>}
      {(creation.error ?? assignment.error ?? remove.error) && (
        <ErrorNote error={creation.error ?? assignment.error ?? remove.error} />
      )}
      {inventory.isPending ? (
        <Spinner />
      ) : inventory.error ? (
        <ErrorNote error={inventory.error} />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {inventory.data.items.map((domain) => (
              <Card key={domain.id}>
                <CardHeader
                  title={
                    domain.zoneId ? (
                      <Link
                        className="break-all text-blue-600"
                        to={`?${new URLSearchParams({ ...Object.fromEntries(params), zoneId: domain.zoneId })}`}
                      >
                        {domain.name}
                      </Link>
                    ) : (
                      domain.name
                    )
                  }
                  description={`${domain.recordCount} records · ${domain.subscriptionName}${domain.customerName ? ` · ${domain.customerName}` : ''}`}
                />
                <div className="space-y-3 px-5 pb-4">
                  <Publication value={domain.publication} label="Public publishing" />
                  {!domain.zoneId && (
                    <>
                      <Button
                        disabled={!domain.capabilities.createZone.allowed}
                        loading={creation.isPending}
                        onClick={() =>
                          domain.capabilities.manageSettings.allowed
                            ? setConfigureZone(domain)
                            : creation.mutate(domain)
                        }
                      >
                        Create zone
                      </Button>
                      {domain.capabilities.createZone.reason && (
                        <p className="text-sm text-slate-500">
                          {domain.capabilities.createZone.reason}
                        </p>
                      )}
                      {domain.capabilities.removeAssignment.allowed && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            if (window.confirm(`Remove unused assignment ${domain.name}?`))
                              remove.mutate(domain.id);
                          }}
                        >
                          Remove assignment
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
          {!inventory.data.total && <p>No assigned domains match this view.</p>}
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              disabled={inventory.data.page <= 1}
              onClick={() => change({ page: String(inventory.data.page - 1) })}
            >
              Previous
            </Button>
            <p className="text-sm">
              Page {inventory.data.page} · {inventory.data.total} domains
            </p>
            <Button
              variant="secondary"
              disabled={inventory.data.page * inventory.data.pageSize >= inventory.data.total}
              onClick={() => change({ page: String(inventory.data.page + 1) })}
            >
              Next
            </Button>
          </div>
        </>
      )}
      {configureZone && (
        <Modal
          title={`Create zone for ${configureZone.name}`}
          open
          onClose={() => setConfigureZone(null)}
        >
          <ZoneForm
            assignment={configureZone}
            subscriptionId={configureZone.subscriptionId}
            onCreated={(id) => change({ zoneId: id })}
            onDone={() => setConfigureZone(null)}
          />
        </Modal>
      )}
      <Modal title="Assign domain" open={assign} onClose={() => setAssign(false)}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            assignment.mutate();
          }}
        >
          <Label htmlFor="dns-assigned-name">Domain name</Label>
          <Input
            id="dns-assigned-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="example.com"
          />
          <Label htmlFor="dns-assigned-subscription">Subscription</Label>
          <Select
            id="dns-assigned-subscription"
            required
            value={assignmentSub || params.get('subscriptionId') || ''}
            onChange={(e) => setAssignmentSub(e.target.value)}
          >
            <option value="">Select subscription</option>
            {subscriptions.data?.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </Select>
          <p className="text-sm text-slate-500">
            This authorizes creation of a DNS zone. Public publishing and registrar delegation still
            require provider setup.
          </p>
          {assignment.error && <ErrorNote error={assignment.error} />}
          <Button type="submit" loading={assignment.isPending}>
            Assign domain
          </Button>
        </form>
      </Modal>
    </div>
  );
}
